import React, { useRef, useEffect, useState } from 'react';
import { View, ScrollView, Text, Platform, StyleSheet, ImageBackground, TouchableOpacity } from 'react-native';
import type { Tutor, SupportedLanguage } from '../types';
import type { Message } from '../types';
import { LoadingAIResponseChat } from './LoadingAIResponseChat';
import { AIMessage } from './AIMessage';
import { UserMessage } from './UserMessage';
import { useChatContext } from '../contexts/ChatContext';
import { useSettings } from '../contexts/SettingsContext';
import { TranslationKey } from '../i18n/translations';
import { ConfirmationModal } from './ConfirmationModal';

interface ChatMessagesProps {
  getTranslation: (key: TranslationKey, lang?: SupportedLanguage) => string;
  tutor?: Tutor;
}

import { NewConversationButton } from './NewConversationButton';

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  getTranslation,
  tutor,
}) => {
  const { interfaceLanguage } = useSettings();
  const {
    messages,
    isWaitingForAI,
    clearMessages,
  } = useChatContext();

  const scrollViewRef = useRef<ScrollView>(null);
  const messagesEndRef = useRef<View>(null);
  const [scrollY, setScrollY] = useState(0);
  const scrollToBottom = () => {
    if (Platform.OS === 'web') {
      const element = messagesEndRef.current as unknown as HTMLElement;
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }
  };

  useEffect(() => {
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [messages, isWaitingForAI]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleScroll = () => {
        const container = document.querySelector('.messages-scroll-container');
        if (container) {
          setScrollY(container.scrollTop);
        }
      };

      const container = document.querySelector('.messages-scroll-container');
      if (container) {
        container.addEventListener('scroll', handleScroll);
      }

      return () => {
        if (container) {
          container.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, []);

  const renderMessage = (message: Message) => {
    if (message.type === 'user') {
      return (
        <UserMessage
          key={message.id}
          message={message}
        />
      );
    }

    if (!tutor) {
      console.warn('No tutor available for AI message');
      return null;
    }

    return (
      <AIMessage
        key={message.id}
        message={message}
        tutor={message.tutor || tutor}
        isFromNewResponse={message.isFromNewResponse || false}
      />
    );
  };

  if (!tutor) {
    return (
      <View style={styles.container}>
        <Text style={styles.noTutorText}>Please select a tutor to start chatting</Text>
      </View>
    );
  }

  const backgroundTransform = Platform.OS === 'web' 
    ? `translateY(${scrollY * 0.5}px)` 
    : undefined;

  return (
    <View style={styles.container}>
      <View style={styles.backgroundContainer}>
        <ImageBackground 
          source={require('@assets/background.webp')} 
          style={[
            styles.backgroundImage,
            Platform.OS === 'web' && {
              transform: backgroundTransform,
            }
          ]}
          resizeMode="cover"
          imageStyle={{ opacity: 0.075 }}
        />
      </View>
      <ScrollView
        ref={scrollViewRef}
        style={styles.messagesContainer}
        contentContainerStyle={styles.messagesContent}
        {...(Platform.OS === 'web' ? { className: 'messages-scroll-container' } : {})}
        onScroll={Platform.OS !== 'web' ? (event) => {
          setScrollY(event.nativeEvent.contentOffset.y);
        } : undefined}
        scrollEventThrottle={16}
      >
        {messages?.map(message => renderMessage(message))}
        {isWaitingForAI && (
          <LoadingAIResponseChat
            getTranslation={getTranslation}
          />
        )}
        <View ref={messagesEndRef} style={{ height: 1 }} />
        {messages.length > 0 && (
          <NewConversationButton
            onNewConversation={clearMessages}
            getTranslation={getTranslation}
            interfaceLanguage={interfaceLanguage}
          />
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'absolute',
    transition: 'transform 0.1s ease-out',
  },
  messagesContainer: {
    flex: 1,
    width: '100%',
    position: 'relative',
    zIndex: 1,
  },
  messagesContent: {
    flexGrow: 1,
    width: '100%',
    paddingVertical: 16,
    paddingHorizontal: 0,
  },
  errorContainer: {
    padding: 16,
    backgroundColor: '#ffebee',
    borderRadius: 8,
    marginHorizontal: 16,
    marginBottom: 8,
  },
  errorText: {
    color: '#d32f2f',
    fontSize: 14,
  },
  noTutorText: {
    textAlign: 'center',
    color: '#666',
    fontSize: 16,
    marginTop: 24,
  },
  newConversationButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderWidth: 1,
    borderColor: '#ADD8E6',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignSelf: 'center',
    marginTop: 16,
  },
  newConversationButtonText: {
    color: '#007AFF',
    fontSize: 14,
    fontWeight: '500',
  },
});
